import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { Loader2, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { apiFetch, ApiError } from "@/lib/api-client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const joinTenantSchema = z.object({
  joinCode: z
    .string()
    .min(8, "El codigo debe tener 8 caracteres")
    .max(8, "El codigo debe tener 8 caracteres")
    .regex(/^[A-Za-z0-9]+$/, "El codigo solo puede contener letras y numeros"),
  message: z.string().max(500, "El mensaje es muy largo").optional(),
});

type JoinTenantValues = z.infer<typeof joinTenantSchema>;

interface JoinRequestResult {
  tenantId: string;
  tenantName: string;
  status: string;
  createdAt: string;
}

interface JoinTenantFormProps {
  onSuccess: (request: JoinRequestResult) => void;
}

export function JoinTenantForm({ onSuccess }: JoinTenantFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<JoinTenantValues>({
    resolver: zodResolver(joinTenantSchema),
    defaultValues: {
      joinCode: "",
      message: "",
    },
  });

  const onSubmit = async (data: JoinTenantValues) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await apiFetch.post<{ tenantId: string; tenantName: string }>(
        "/api/onboarding/join-request",
        data
      );
      onSuccess({
        tenantId: result.tenantId,
        tenantName: result.tenantName,
        status: "pending",
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isNotFound()) {
          setError("Codigo de invitacion invalido");
        } else if (err.isStatus(409)) {
          setError("Ya tienes una solicitud pendiente");
        } else {
          setError(err.message);
        }
      } else {
        setError("Error desconocido");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 rounded-full bg-primary/10 p-3 w-fit">
          <Users className="h-6 w-6 text-primary" />
        </div>
        <CardTitle>Unirme a una empresa</CardTitle>
        <CardDescription>
          Ingresa el codigo de invitacion que te proporcionaron
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="joinCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Codigo de invitacion</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="ABCD1234"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase())
                      }
                      maxLength={8}
                      disabled={isSubmitting}
                      className="text-center text-lg tracking-widest font-mono"
                    />
                  </FormControl>
                  <FormDescription>
                    8 caracteres alfanumericos
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mensaje (opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Hola, soy contador y me gustaria unirme a su equipo..."
                      {...field}
                      disabled={isSubmitting}
                      rows={3}
                    />
                  </FormControl>
                  <FormDescription>
                    Un mensaje para el administrador
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {error && (
              <div className="text-sm text-destructive text-center">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar solicitud"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
